import { useCallback, useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { useFlowState } from '@betterment/js-runtime';
import { useNotifications } from '@betterment/shared-components';
import { useNavigate } from 'react-router-dom';

import { useBillingPlanStorage } from './useBillingPlansStorage';

import { makeUpsertBillingPlanInput } from '../api';
import { BillingPlanFlowValues, isExistingPlanWithClients } from '../data';
import { UpsertBillingPlanDocument } from '../graphql/mutation.upsertBillingPlan.generated';

export function useSubmitHandler() {
  const navigate = useNavigate();
  const [upsertBillingPlan] = useGraphQLMutation(UpsertBillingPlanDocument);
  const {
    goToNextStep,
    flowState: { currentStep },
  } = useFlowState<BillingPlanFlowValues>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { saveValues: saveSessionStorageValues } = useBillingPlanStorage();
  const { setNotification } = useNotifications();

  const submitHandler = useCallback(
    async (values: BillingPlanFlowValues) => {
      const successMessage = values.billingPlanId
        ? `Changes to "${values.planName}" have been saved.`
        : 'Your new billing plan has been created.';

      // I would like this to be (if last step) but the getNextStep function doesn't work with conditional steps
      const stepsBeforeReviewAffectedHouseholds = [
        'asset_based_configuration',
        'fixed_based_configuration',
        'tiered_configuration',
      ];
      if (stepsBeforeReviewAffectedHouseholds.includes(currentStep) && isExistingPlanWithClients(values)) {
        saveSessionStorageValues(values);
        goToNextStep(values);
      } else {
        try {
          setErrorMessage(null);

          const fetchResult = await upsertBillingPlan({
            variables: {
              input: {
                billingPlan: makeUpsertBillingPlanInput(values),
              },
            },
          });

          // @ts-expect-error TS18048 violates strict null checks
          if (fetchResult.data?.upsertBillingPlan?.displayableErrors?.length > 0) {
            // @ts-expect-error TS18049 violates strict null checks
            const errorMessages = fetchResult.data.upsertBillingPlan.displayableErrors
              .map(error => error.message)
              .join(' ');
            setErrorMessage(errorMessages);
          } else {
            setNotification({ type: 'success', message: successMessage });
            void navigate('/client_billing/billing_plans');
          }
        } catch (_) {
          setErrorMessage(`An unexpected error occurred while saving the billing plan`);
        }
      }
    },
    [currentStep, goToNextStep, navigate, saveSessionStorageValues, setNotification, upsertBillingPlan, setErrorMessage]
  );

  return {
    errorMessage,
    submitHandler,
  };
}
